if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');

        // If there's an initial querySelector('[name=id]') that was disabled earlier,
        // make sure it's enabled so we can set value.
        const initialIdEl = this.form && this.form.querySelector('[name="id"]');
        if (initialIdEl) initialIdEl.disabled = false;

        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        // State
        this._observer = null;
        this._triedSelecting = false;

        // Try to select on init (if variants already present)
        this.ensureVariantSelected();

        // If variant inputs/options are added later, watch and ensure selection
        this.setupVariantObserver();
      }

      // Ensure a variant is selected - called on init and before submit
      ensureVariantSelected() {
        // Prevent repeated heavy work
        if (this._triedSelecting) return;
        this._triedSelecting = true;

        const selected = this.getSelectedVariantInput();
        if (selected && selected.value) return; // already selected

        // Try select-type first
        const select = this.form.querySelector('select[name="id"]');
        if (select) {
          // find first not-disabled option with a value
          const opt = Array.from(select.options).find(o => o.value && !o.disabled);
          if (opt) {
            select.value = opt.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
            return;
          }
        }

        // Try radio / inputs type
        const radios = Array.from(this.form.querySelectorAll('input[name="id"]'));
        if (radios.length) {
          const firstAvailable = radios.find(r => !r.disabled && r.value);
          if (firstAvailable) {
            firstAvailable.checked = true;
            firstAvailable.dispatchEvent(new Event('change', { bubbles: true }));
            return;
          }
        }

        // If no selection possible now, leave it for observer to handle later.
      }

      // Return the input/select element representing the chosen variant (if any)
      getSelectedVariantInput() {
        const select = this.form.querySelector('select[name="id"]');
        if (select) {
          const val = select.value;
          if (val) return select;
        }
        const checkedInput = this.form.querySelector('input[name="id"]:checked');
        if (checkedInput) return checkedInput;
        // fallback: first input[name="id"]
        return this.form.querySelector('input[name="id"]') || null;
      }

      // Attach a MutationObserver to watch for changes to the form (variants rendered later)
      setupVariantObserver() {
        // If inputs already present, no need to observe
        const hasVariantsNow = this.form.querySelector('select[name="id"]') || this.form.querySelectorAll('input[name="id"]').length;
        if (hasVariantsNow) return;

        // Setup observer to watch for child additions where variant controls may appear
        const observerTarget = this.form;
        if (!observerTarget || !window.MutationObserver) return;

        this._observer = new MutationObserver((mutations, obs) => {
          // if we now have variant controls, ensure selection and disconnect observer
          const hasNow = observerTarget.querySelector('select[name="id"]') || observerTarget.querySelectorAll('input[name="id"]').length;
          if (hasNow) {
            this.ensureVariantSelected();
            if (this._observer) {
              this._observer.disconnect();
              this._observer = null;
            }
          }
        });

        this._observer.observe(observerTarget, { childList: true, subtree: true });
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // --- Ensure a variant is selected right before submit ---
        // This re-checks and forces selection synchronously if possible.
        const selectedBefore = this.getSelectedVariantInput();
        if (!selectedBefore || !selectedBefore.value) {
          // Try forcibly selecting now (in case constructor/observer missed)
          // Try select dropdown first
          const variantSelect = this.form.querySelector('select[name="id"]');
          if (variantSelect) {
            const firstAvailableOpt = Array.from(variantSelect.options).find(opt => opt.value && !opt.disabled);
            if (firstAvailableOpt) {
              variantSelect.value = firstAvailableOpt.value;
              variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }

          // Try radio inputs
          const radios = Array.from(this.form.querySelectorAll('input[name="id"]'));
          if (radios.length && !radios.some(r => r.checked && r.value)) {
            const firstAvailable = radios.find(r => !r.disabled && r.value);
            if (firstAvailable) {
              firstAvailable.checked = true;
              firstAvailable.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        }

        // Re-evaluate after forced selection
        const finalSelected = this.getSelectedVariantInput();
        const selectedValue = finalSelected && finalSelected.value;

        if (!selectedValue) {
          // Show friendly error and stop submit; same UI pattern as before
          this.handleErrorMessage("Please select a size or option before adding to cart.");
          return;
        }
        // --- End ensure variant selection ---

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        const spinner = this.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              if (response && response.description && response.description == "Required parameter missing or invalid: id") {
                this.handleErrorMessage("Please select a size from the options above.");
              } else {
                this.handleErrorMessage(response.description);
              }

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              const span = this.submitButton.querySelector('span');
              if (span) span.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            const spinner2 = this.querySelector('.loading__spinner');
            if (spinner2) spinner2.classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.parentElement.parentElement.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
